// The handful of shadcn-style primitives this app actually uses, copied in rather than pulled from
// a component library so the look is entirely ours. Radix supplies the accessible behaviour
// (focus trapping, escape handling, portalling); the styling is local.

import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { X } from "lucide-react";
import * as React from "react";
import { cn } from "../../lib/ui.js";

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "ghost" | "danger";
  size?: "sm" | "md";
};

export function Button({ className, variant = "secondary", size = "md", ...props }: ButtonProps) {
  return (
    <button
      {...props}
      className={cn(
        "inline-flex items-center justify-center gap-1.5 rounded-md border font-medium transition",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brass",
        // pointer-events-none so hovers reach the Tooltip's wrapper around a disabled button; the
        // not-allowed cursor moves there.
        "disabled:pointer-events-none disabled:opacity-40",
        size === "sm" ? "px-2 py-1 text-xs" : "px-3 py-1.5 text-sm",
        variant === "primary" && "border-brass/70 bg-brass/20 text-parchment hover:bg-brass/30",
        variant === "secondary" && "border-ink-line bg-ink-soft text-parchment hover:border-brass/50",
        variant === "ghost" && "border-transparent bg-transparent text-parchment-dim hover:text-parchment",
        variant === "danger" && "border-evil/70 bg-evil/20 text-parchment hover:bg-evil/35",
        className,
      )}
    />
  );
}

export const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...props }, ref) {
    return (
      <input
        ref={ref}
        {...props}
        className={cn(
          "w-full rounded-md border border-ink-line bg-ink px-3 py-1.5 text-sm text-parchment",
          "placeholder:text-parchment-dim/60 focus:border-brass/60 focus:outline-none",
          className,
        )}
      />
    );
  },
);

export function Textarea({ className, ...props }: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...props}
      className={cn(
        "w-full resize-none rounded-md border border-ink-line bg-ink px-3 py-2 text-sm text-parchment",
        "placeholder:text-parchment-dim/60 focus:border-brass/60 focus:outline-none",
        className,
      )}
    />
  );
}

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;

export function DialogContent({
  className,
  title,
  description,
  children,
  onOpenAutoFocus,
}: {
  className?: string;
  /** Usually a name, but takes a node so a caller can fold a subtitle onto the same line. */
  title: React.ReactNode;
  description?: string;
  children: React.ReactNode;
  /**
   * Radix focuses the first control on open, and a tooltip on it would cover the title; pass a
   * handler to move focus elsewhere.
   */
  onOpenAutoFocus?: (event: Event) => void;
}) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm" />
      <DialogPrimitive.Content
        className={cn(
          "fixed left-1/2 top-1/2 z-50 w-[min(32rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2",
          "max-h-[85vh] overflow-y-auto rounded-lg border border-ink-line bg-ink-soft p-5 shadow-2xl cc-scroll",
          className,
        )}
        {...(onOpenAutoFocus ? { onOpenAutoFocus } : {})}
      >
        <DialogPrimitive.Title className="flex items-baseline gap-2 text-base font-semibold text-parchment">
          {title}
        </DialogPrimitive.Title>
        {description ? (
          <DialogPrimitive.Description className="mt-1 text-xs text-parchment-dim">
            {description}
          </DialogPrimitive.Description>
        ) : (
          // Radix warns without a description; an empty one opts out.
          <DialogPrimitive.Description className="sr-only">{title}</DialogPrimitive.Description>
        )}
        <div className="mt-4">{children}</div>
        <DialogPrimitive.Close
          aria-label="Close"
          className="absolute right-3 top-3 rounded p-1 text-parchment-dim hover:text-parchment"
        >
          <X className="size-4" />
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

/**
 * Tooltips use `aria-describedby` (via Radix) rather than `title`, which would replace the
 * control's accessible name. An enabled control is its own trigger; a disabled one is wrapped, so
 * its explanation is mouse-only.
 */
export function Tooltip({
  content,
  disabled = false,
  side = "top",
  children,
}: {
  content: React.ReactNode;
  /** Pass the target's own disabled state so the trigger can be wrapped instead. */
  disabled?: boolean;
  side?: "top" | "right" | "bottom" | "left";
  children: React.ReactNode;
}) {
  const [open, setOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  // Radix closes on pointer-leave or blur only; also close on scroll or a modal opening over the
  // trigger, which produce neither.
  React.useEffect(() => {
    if (!open) return;
    const close = (): void => setOpen(false);
    const onMove = (): void => {
      if (triggerRef.current && !triggerRef.current.matches(":hover")) close();
    };
    document.addEventListener("scroll", close, true);
    document.addEventListener("pointermove", onMove);
    return () => {
      document.removeEventListener("scroll", close, true);
      document.removeEventListener("pointermove", onMove);
    };
  }, [open]);

  if (!content) return <>{children}</>;
  return (
    <TooltipPrimitive.Root open={open} onOpenChange={setOpen}>
      <TooltipPrimitive.Trigger
        asChild
        ref={triggerRef}
        // Focus handed back after a dialog closes is not a keyboard user arriving, and the tooltip
        // it opened would sit there until something else took focus. Only keyboard focus opens one.
        onFocus={(event) => {
          if (!event.currentTarget.matches(":focus-visible")) event.preventDefault();
        }}
      >
        {disabled ? <span className="inline-flex cursor-not-allowed">{children}</span> : children}
      </TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          sideOffset={6}
          collisionPadding={8}
          className="z-50 max-w-64 rounded-md border border-ink-line bg-ink px-2.5 py-1.5 text-xs leading-snug text-parchment shadow-xl"
        >
          {content}
          <TooltipPrimitive.Arrow className="fill-ink-line" />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}

export const TooltipProvider = TooltipPrimitive.Provider;

export function Label({ className, ...props }: React.LabelHTMLAttributes<HTMLLabelElement>) {
  return <label {...props} className={cn("text-xs uppercase tracking-wide text-parchment-dim", className)} />;
}
